import { Fragment, type ReactNode } from 'react';
import { testIdProp } from '@givmo/design-system';

export type TermsBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'blockquote'; text: string }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'rule' };

type PendingBlock =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'blockquote'; lines: string[] };

/**
 * Conservatively recognizes only the structural syntax used by Charity Terms.
 * Anything else remains literal text for the paragraph presenter.
 *
 * Recognized: `#`/`##`/`###` headings, `---` rules, `- `/`* ` bullets, `N. ` numbered items, `> `
 * quotes, GitHub-style pipe tables, and paragraphs. A pipe table is the one construct that needs
 * LOOKAHEAD — a run of `|` lines is a table only when its SECOND line is a separator row — so the
 * scan is indexed rather than a plain for-of.
 */
export function splitTermsBlocks(text: string): TermsBlock[] {
  const blocks: TermsBlock[] = [];
  const lines = text.split(/\r\n|\n|\r/);
  let pending: PendingBlock | null = null;

  const flushPending = () => {
    if (!pending) return;

    if (pending.kind === 'paragraph' || pending.kind === 'blockquote') {
      blocks.push({ kind: pending.kind, text: pending.lines.join('\n') });
    } else {
      blocks.push(pending);
    }
    pending = null;
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]!;

    if (line === '') {
      flushPending();
      continue;
    }

    const heading = /^(#{1,3}) (.*)$/.exec(line);
    if (heading) {
      flushPending();
      blocks.push({
        kind: 'heading',
        level: heading[1]!.length as 1 | 2 | 3,
        text: heading[2]!,
      });
      continue;
    }

    if (line === '---') {
      flushPending();
      blocks.push({ kind: 'rule' });
      continue;
    }

    // Before the bullet rule: a `|` line never starts a bullet, and a table that fails to match must
    // fall through to the paragraph presenter exactly as it did before tables were recognized.
    if (line.startsWith('|')) {
      const table = readTable(lines, index);
      if (table) {
        flushPending();
        blocks.push(table.block);
        index = table.next - 1;
        continue;
      }
    }

    const bullet = /^[-*] (.*)$/.exec(line);
    if (bullet) {
      if (pending?.kind !== 'list' || pending.ordered) {
        flushPending();
        pending = { kind: 'list', ordered: false, items: [] };
      }
      pending.items.push(bullet[1]!);
      continue;
    }

    const numbered = /^\d+\. (.*)$/.exec(line);
    if (numbered) {
      if (pending?.kind !== 'list' || !pending.ordered) {
        flushPending();
        pending = { kind: 'list', ordered: true, items: [] };
      }
      pending.items.push(numbered[1]!);
      continue;
    }

    const quote = /^> (.*)$/.exec(line);
    if (quote) {
      if (pending?.kind !== 'blockquote') {
        flushPending();
        pending = { kind: 'blockquote', lines: [] };
      }
      pending.lines.push(quote[1]!);
      continue;
    }

    if (pending?.kind !== 'paragraph') {
      flushPending();
      pending = { kind: 'paragraph', lines: [] };
    }
    pending.lines.push(line);
  }

  flushPending();
  return blocks;
}

/** A separator cell: dashes, optionally colon-anchored. Cells are trimmed before this is applied. */
const SEPARATOR_CELL = /^:?-+:?$/;

/**
 * The pipe table starting at `start`, or `null` when the run is not one.
 *
 * The run is every consecutive line beginning with `|`; it is a TABLE only when the run's SECOND
 * line is a GFM separator row. Without that row the caller leaves every line literal — prose that
 * happens to open with a pipe must not become a one-cell table, and a legal instrument is the last
 * place to guess. The separator is the whole test on purpose: requiring its cell count to match the
 * header would turn a served table into raw pipes over a typo, which is the defect this cures.
 */
function readTable(
  lines: string[],
  start: number,
): { block: Extract<TermsBlock, { kind: 'table' }>; next: number } | null {
  let end = start;
  while (end < lines.length && lines[end]!.startsWith('|')) end += 1;
  if (end - start < 2) return null;

  const separator = splitRow(lines[start + 1]!);
  if (separator.length === 0 || !separator.every((cell) => SEPARATOR_CELL.test(cell))) return null;

  const header = splitRow(lines[start]!);
  // Short rows are padded to the header's width. Nothing served is dropped or rewritten — an added
  // cell is the empty string — but the grid stays aligned, and on a consent table a body cell that
  // slides under the wrong heading is a misreading of the agreement, not a cosmetic defect.
  const rows = lines.slice(start + 2, end).map((row) => {
    const cells = splitRow(row);
    while (cells.length < header.length) cells.push('');
    return cells;
  });

  return { block: { kind: 'table', header, rows }, next: end };
}

/** One pipe row's cells: split on `|`, trimmed, without the empties the outer pipes produce. */
function splitRow(line: string): string[] {
  const cells = line.split('|').map((cell) => cell.trim());
  if (cells[0] === '') cells.shift();
  if (cells.length > 0 && cells[cells.length - 1] === '') cells.pop();
  return cells;
}

export function TermsBody({ text, testId }: { text: string; testId?: string }) {
  return (
    <div
      className="mx-auto max-w-[760px] font-sans text-s14 leading-h15 text-text-strong"
      {...testIdProp(testId)}
    >
      {splitTermsBlocks(text).map((block, index) => (
        <TermsBlockView key={index} block={block} />
      ))}
    </div>
  );
}

function TermsBlockView({ block }: { block: TermsBlock }) {
  if (block.kind === 'heading') {
    if (block.level === 1) {
      return <h2 className="mb-16 text-s24 font-w700">{renderInline(block.text)}</h2>;
    }
    if (block.level === 2) {
      return <h2 className="mb-10 mt-20 text-s18 font-w700">{renderInline(block.text)}</h2>;
    }
    return <h3 className="mb-8 mt-16 text-s16 font-w600">{renderInline(block.text)}</h3>;
  }

  if (block.kind === 'paragraph') {
    return <p className="mb-12 whitespace-pre-line">{renderInlineLines(block.text)}</p>;
  }

  if (block.kind === 'blockquote') {
    return (
      <blockquote className="my-12 whitespace-pre-line border-l-2 border-border pl-16">
        {renderInlineLines(block.text)}
      </blockquote>
    );
  }

  if (block.kind === 'rule') {
    return <hr className="my-20 border-border" />;
  }

  if (block.kind === 'table') {
    return <TermsTableView block={block} />;
  }

  const List = block.ordered ? 'ol' : 'ul';
  return (
    <List className={`my-12 pl-24 ${block.ordered ? 'list-decimal' : 'list-disc'}`}>
      {block.items.map((item, index) => (
        <li key={index} className="mb-4">
          {renderInline(item)}
        </li>
      ))}
    </List>
  );
}

/**
 * A served table, rendered as a real `<table>` so its cells keep their header association.
 *
 * ⚠ THE COLUMNS STAY VISIBLE BEFORE THEY SCROLL. The wrapper owns horizontal overflow so a wide
 * table can never push the page sideways at phone width, but the table itself is `w-full` with no
 * minimum — a table in an `overflow-x-auto` box fails QUIETLY on a phone (the estate paid for this
 * once on the org roster: columns parked off the right edge behind a scroll nothing announces), and
 * a reader who cannot see the consent column has not read the agreement. So narrow widths WRAP, and
 * the scroll is the safety net for a table that genuinely cannot shrink further.
 *
 * The header cells are NOT the design system's `TH` (uppercase, `text-s11`, `whitespace-nowrap`):
 * these are sentence-case sentences from a signed instrument, and case is not this presenter's to
 * change. Same reason there is no zebra fill, no badge and no icon — the served em-dash IS the
 * empty value.
 *
 * `first:pl-0 last:pr-0` is what keeps the table in the document's text column: cell padding on the
 * outer cells would indent the grid a few pixels off the prose above it, which reads as a pasted-in
 * fragment rather than part of the agreement. Neither cell names a text colour — both inherit the
 * document's, because a term a reader has to act on must not be quieter than the prose around it,
 * and weight alone is what separates a heading cell from a value.
 */
function TermsTableView({ block }: { block: Extract<TermsBlock, { kind: 'table' }> }) {
  return (
    <div className="my-16 w-full overflow-x-auto">
      <table className="w-full border-collapse text-left text-s13">
        <thead>
          <tr>
            {block.header.map((cell, index) => (
              <th
                key={index}
                scope="col"
                className="border-b border-border-strong px-10 py-8 align-bottom font-w600 first:pl-0 last:pr-0"
              >
                {renderInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="border-b border-border-subtle px-10 py-8 align-top first:pl-0 last:pr-0"
                >
                  {renderInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderInlineLines(text: string): ReactNode {
  return text.split('\n').map((line, index) => (
    <Fragment key={index}>
      {index > 0 ? '\n' : null}
      {renderInline(line)}
    </Fragment>
  ));
}

/**
 * Render bold and italics only when every marker on this line forms an unambiguous pair.
 *
 * TWO PASSES, `**` FIRST. The bold pass consumes the double markers, so the italic pass that runs
 * inside each of its runs can never mistake half of a `**` for an emphasis marker. Either pass
 * finding anything ambiguous — a marker touching another `*`, an odd count, an empty pair — leaves
 * the WHOLE LINE literal. That discipline predates italics and is the point: a legal instrument
 * showing its source markers is a reader's problem, while one that half-renders them is a document
 * that says something the signed bytes do not.
 *
 * A pair that straddles a bold boundary (`*opening **bold** closing*`) reads as unbalanced inside
 * each run and therefore stays literal, which is the conservative answer for a construct the served
 * instrument does not use.
 */
function renderInline(text: string): ReactNode {
  if (!text.includes('*')) return text;

  const boldMarkers = pairedMarkers(text, '**');
  if (boldMarkers === null) return text;

  const boldRuns = markerRuns(text, boldMarkers, 2);
  const italicRuns = boldRuns.map((run) => {
    const markers = pairedMarkers(run.text, '*');
    return markers === null ? null : markerRuns(run.text, markers, 1);
  });
  if (italicRuns.some((runs) => runs === null)) return text;

  return boldRuns.map((run, runIndex) => {
    const inner = italicRuns[runIndex]!.map((piece, pieceIndex) =>
      piece.emphasized ? (
        <em key={pieceIndex}>{piece.text}</em>
      ) : (
        <Fragment key={pieceIndex}>{piece.text}</Fragment>
      ),
    );
    return run.emphasized ? (
      <strong key={runIndex}>{inner}</strong>
    ) : (
      <Fragment key={runIndex}>{inner}</Fragment>
    );
  });
}

/**
 * The offsets of every `marker` in `text` when they form unambiguous pairs, else `null`.
 *
 * Ambiguous is: a marker touching another `*` on either side (so `****` and `***word***` stay
 * literal), an odd number of markers, or a pair with nothing between them.
 */
function pairedMarkers(text: string, marker: '**' | '*'): number[] | null {
  const offsets: number[] = [];
  let searchFrom = 0;

  while (true) {
    const at = text.indexOf(marker, searchFrom);
    if (at === -1) break;
    if (text[at - 1] === '*' || text[at + marker.length] === '*') return null;
    offsets.push(at);
    searchFrom = at + marker.length;
  }

  if (offsets.length % 2 !== 0) return null;
  for (let index = 0; index < offsets.length; index += 2) {
    if (offsets[index + 1] === offsets[index]! + marker.length) return null;
  }
  return offsets;
}

/** `text` cut into alternating plain and emphasized runs at `markers`; empty runs are dropped. */
function markerRuns(
  text: string,
  markers: number[],
  markerLength: number,
): { text: string; emphasized: boolean }[] {
  const runs: { text: string; emphasized: boolean }[] = [];
  const push = (value: string, emphasized: boolean) => {
    if (value !== '') runs.push({ text: value, emphasized });
  };

  let cursor = 0;
  for (let index = 0; index < markers.length; index += 2) {
    const opening = markers[index]!;
    const closing = markers[index + 1]!;
    push(text.slice(cursor, opening), false);
    push(text.slice(opening + markerLength, closing), true);
    cursor = closing + markerLength;
  }
  push(text.slice(cursor), false);
  return runs;
}
